import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import SyncIndicator from './SyncIndicator';
import BrandLogo from './BrandLogo';
import {
  Activity,
  BarChart3,
  FlaskConical,
  Sparkles,
  Bell,
  BookOpen,
  Building2,
  ChartColumn,
  ChevronRight,
  ClipboardCheck,
  ClipboardList,
  Factory,
  FileText,
  Fuel,
  Handshake,
  Home,
  IdCard,
  Key,
  KeyRound,
  Landmark,
  Layers,
  LayoutDashboard,
  ListChecks,
  LogOut,
  Map,
  MapPinned,
  Menu,
  MessageCircle,
  Moon,
  MoreHorizontal,
  PackageCheck,
  Radar,
  Receipt,
  Rocket,
  Scale,
  ScanSearch,
  ScrollText,
  Server,
  Settings,
  Shield,
  ShieldAlert,
  ShoppingCart,
  Shuffle,
  Sun,
  Tags,
  Truck,
  User,
  Users,
  ShieldCheck,
  Wallet,
  Warehouse,
  Wrench,
  Boxes,
  Undo2,
  HandCoins,
  Download,
} from 'lucide-react';
import { cn, PACK_LABELS } from '../lib/utils';
import { Button } from './ui/button';
import db from '../lib/db';
import { adminAPI, licensesAPI } from '../lib/api';
import {
  CONTROL_FUEL_NAV_ITEMS,
  LOTS_NAV_ITEMS,
  STATION_NAV_ITEMS,
  societeAStations,
  isControlFuelPrimaryPack,
  isStationsPrimaryPack,
} from '../lib/entitlements';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  SheetClose,
} from './ui/sheet';
import { motion } from 'framer-motion';
import { Citerne, Convoi } from './ui/icons/domain-icons';
import { TopBar } from './shell/TopBar';
import { AssistantChat } from './assistant/AssistantChat';
// One catalogue for role names; this file used to keep a fourth stale copy.
import { ROLE_LABELS } from '../lib/roles';
import { LEXIQUE } from '../lib/lexique';
import { useRafraichirSur } from '../hooks/useRafraichirSur';
import { ONBOARDING_ROUTE, isOnboardingDone } from './onboarding';

/** La version affichée au pied de page vient du paquet: recopiée à la main,
 *  elle resterait à « 1.0.0 » pendant trois ans et ne servirait plus à
 *  identifier ce qui tourne chez un client au téléphone. */
const APP_VERSION = process.env.REACT_APP_VERSION || '0.0.0-dev';

/**
 * Le menu du pack Transport, rangé selon la vie d'un camion dans la société:
 * on possède ou on loue des camions, on les envoie charger du carburant, on
 * vend ce carburant, on encaisse et on paie. Cinq groupes portent chacun un
 * titre en français courant, puis « Plus » garde ce qui se règle rarement.
 *
 * Un seul constructeur pour le bureau et pour le téléphone: la colonne de
 * gauche et la feuille « Plus » listent ainsi exactement les mêmes écrans,
 * sans qu'une règle de rôle puisse être corrigée d'un côté et oubliée de
 * l'autre. Les conditions de rôle, de drapeau et de module sont celles de
 * l'ancien menu, reprises à l'identique: seuls le regroupement et les
 * libellés changent.
 */
/**
 * Les écrans du pack Stations dans le groupe « Mes stations ».
 *
 * Les modules station sont dans les modules par défaut du pack Transport et
 * distribution: pour ce pack, ces écrans n'apparaissent que si la société
 * exploite au moins une station. Les autres packs qui ouvrent les stations les
 * gardent pour pouvoir en créer une.
 */
const sectionStationsOuverte = ({ canAccessStations, ownStations, activePack }) =>
  Boolean(canAccessStations) && societeAStations({ ownStations, activePack });

const construireMenuTransport = ({
  isAdminSociete,
  canAccessTransit,
  canAccessLogistics,
  canAccessMaintenance,
  canAccessFuelControl,
  canAccessStations,
  managesDepot = true,
  ownStations = false,
  activePack = null,
}) => {
  const montreStations = sectionStationsOuverte({ canAccessStations, ownStations, activePack });
  const exploitant = isAdminSociete || canAccessTransit;
  const gestion = isAdminSociete || canAccessLogistics;
  const parc = isAdminSociete || canAccessTransit || canAccessLogistics;

  // 1. Mes camions — ceux qu'on possède et ceux qu'on loue.
  const camions = [];
  if (parc) {
    camions.push({ to: '/trucks', icon: Citerne, label: LEXIQUE.menuCamions, module: 'trucks' });
    camions.push({ to: '/drivers', icon: IdCard, label: LEXIQUE.menuChauffeurs, module: 'drivers' });
  }
  // Gardée sur son propre rôle: le MAINTENANCE, dont c'est tout le travail,
  // la voit ici et non cachée dans « Plus ».
  if (parc || canAccessMaintenance) {
    camions.push({ to: '/maintenance', icon: Wrench, label: LEXIQUE.menuEntretien, module: 'maintenance' });
  }
  if (gestion) {
    camions.push({ to: '/rentals', icon: KeyRound, label: LEXIQUE.menuLocations, module: 'truck_rentals' });
  }

  // 2. Envoyer au chargement — du départ à la douane.
  const chargement = [];
  if (isAdminSociete) {
    chargement.push({ to: '/operations', icon: Convoi, label: LEXIQUE.menuConvois, module: 'operations' });
  }
  if (exploitant) {
    chargement.push({ to: '/trips', icon: Map, label: LEXIQUE.menuTrajets, module: 'trips' });
    chargement.push({ to: '/tracking', icon: Radar, label: LEXIQUE.menuSuivi, module: 'gps_tracking' });
    // Ce que le terrain a constaté: attente en douane, blocages, incidents,
    // contrôles de départ.
    chargement.push({ to: '/field-ops', icon: ClipboardCheck, label: LEXIQUE.menuDouane, module: 'trips' });
  }

  // 2 bis. Lots attribués (lot 057) — des camions chargés achetés à un
  // transporteur partenaire : attribution, confirmation, suivi, retours.
  const lots = [];
  if (exploitant || gestion) {
    const iconesLots = iconesLotsAttribues();
    LOTS_NAV_ITEMS.forEach((item) => lots.push({ ...item, icon: iconesLots[item.to] || Layers }));
  }

  // 3. Vendre le carburant — du client à la livraison contrôlée.
  const vente = [];
  if (exploitant) {
    vente.push({ to: '/clients', icon: Handshake, label: LEXIQUE.menuClients, module: 'clients' });
    vente.push({ to: '/orders', icon: ShoppingCart, label: LEXIQUE.menuCommandes, module: 'orders' });
    vente.push({ to: '/dispatch', icon: Shuffle, label: LEXIQUE.menuRepartition, module: 'dispatch_allocations' });
    vente.push({
      to: '/mission-integrity',
      icon: ScanSearch,
      label: LEXIQUE.menuLivraisonsAControler,
      module: 'volume_reconciliation',
    });
  }

  // 3 bis. Mes stations (lot 032) — seulement quand la société exploite au
  // moins une station (exploitée par nous ou confiée à un gérant): accueil des
  // stations, réapprovisionnement par nos camions, réceptions à faire.
  const stations = [];
  if (ownStations && (exploitant || gestion || canAccessStations)) {
    stations.push({ to: '/stations', icon: Fuel, label: 'Accueil stations', module: 'stations' });
    stations.push({ to: '/stations/reapprovisionnement', icon: Truck, label: 'Réapprovisionnement', module: 'stations' });
    stations.push({ to: '/stations/cuves', icon: PackageCheck, label: 'Réceptions à faire', module: 'tanks' });
  }
  // Les autres écrans des stations, remontés de « Plus » (demande du
  // 29 octobre 2026): une seule place pour tout ce qui touche aux stations.
  if (montreStations) {
    const icones = iconesStations();
    const libelles = { '/stations': 'Accueil stations', '/stations/rapports': 'Rapports stations' };
    STATION_NAV_ITEMS
      .filter((item) => !stations.some((deja) => deja.to === item.to))
      .forEach((item) =>
        stations.push({ ...item, icon: icones[item.to] || Activity, label: libelles[item.to] || item.label }),
      );
  }

  // 4. Encaisser et payer — factures, argent, stock, pièces.
  const argent = [];
  if (gestion) {
    argent.push({ to: '/invoices', icon: Receipt, label: LEXIQUE.menuFactures, module: 'invoices' });
    argent.push({ to: '/finance', icon: Wallet, label: LEXIQUE.menuArgent, module: 'finance_cockpit' });
    // Sans dépôt, le stock est le carburant chargé et pas encore livré.
    argent.push({ to: '/stock', icon: Warehouse, label: managesDepot ? LEXIQUE.menuStock : LEXIQUE.menuStockSansDepot, module: 'stock_movements' });
  }
  if (isAdminSociete) {
    argent.push({ to: '/documents', icon: FileText, label: LEXIQUE.menuDocuments, module: 'documents' });
  }

  // 5. Rapports.
  const rapports = [];
  if (gestion) {
    rapports.push({ to: '/rapports', icon: ChartColumn, label: LEXIQUE.menuRapports, module: 'reports' });
    rapports.push({ to: '/daily-reports', icon: ClipboardList, label: LEXIQUE.menuVueDirection, module: 'reports' });
    rapports.push({ to: '/exports', icon: Download, label: LEXIQUE.menuExports, module: 'reports' });
  }

  const groupes = [
    { label: LEXIQUE.groupeCamions, icon: Truck, items: camions },
    { label: LEXIQUE.groupeChargement, icon: Convoi, items: chargement },
    { label: 'Lots attribués', icon: Boxes, items: lots },
    { label: LEXIQUE.groupeVente, icon: ShoppingCart, items: vente },
    { label: LEXIQUE.groupeArgent, icon: Wallet, items: argent },
    // Juste au-dessus des rapports (demande du 29 octobre 2026).
    { label: 'Mes stations', icon: Fuel, items: stations },
    { label: LEXIQUE.groupeRapports, icon: ChartColumn, items: rapports },
  ]
    .filter((groupe) => groupe.items.length)
    .map((groupe) => ({ ...groupe, type: 'section', defaultOpen: true }));

  // « Plus » — ce qui se règle une fois et se consulte rarement.
  const plus = [];
  if (gestion) {
    // La cloche de la barre supérieure porte déjà le compteur d'alertes, mais
    // la liste reste ici pour qui la cherche dans le menu.
    plus.push({ to: '/alerts', icon: Bell, label: LEXIQUE.menuAlertes, module: 'alerts' });
    plus.push({ to: '/pricing-rules', icon: Tags, label: LEXIQUE.menuTarifs, module: 'pricing_rules' });
  }
  if (isAdminSociete) {
    plus.push({ to: '/geo-locations', icon: MapPinned, label: LEXIQUE.menuZones, module: 'gps_tracking' });
    plus.push({ to: '/companies', icon: Landmark, label: LEXIQUE.menuSocietes, module: 'companies' });
    plus.push({ to: '/users', icon: Users, label: LEXIQUE.menuUtilisateurs });
    plus.push({ to: '/acces-clients', icon: KeyRound, label: LEXIQUE.menuAccesClients, module: 'external_integrations' });
    // Pas de `module`: régler ses blocages ne dépend pas du pack acheté.
    plus.push({ to: '/parametres/blocages', icon: ShieldAlert, label: LEXIQUE.menuBlocages });
  }
  if (exploitant) {
    plus.push({ to: '/gps-providers', icon: Settings, label: LEXIQUE.menuFournisseursGps, module: 'gps_tracking' });
  }

  // Sections propres aux autres packs, visibles seulement avec le droit qui
  // y mène: sans cette condition un LOGISTIQUE verrait l'en-tête « Contrôle
  // carburant » alors que rien dans son rôle ne l'y mène.
  const sectionsPacks = [];
  if (canAccessFuelControl) {
    sectionsPacks.push({
      type: 'section',
      label: 'Contrôle carburant',
      icon: Fuel,
      // Même liste et mêmes libellés que le pack Contrôle carburant: une seule
      // source, pour que le menu ne change pas de nom selon le pack acheté.
      items: CONTROL_FUEL_NAV_ITEMS,
    });
  }

  return { groupes, plus, sectionsPacks };
};

/** Icône de chaque entrée du menu Stations (lot 030). */
const iconesStations = () => ({
  '/stations': Home,
  '/stations/poste': Fuel,
  '/stations/cuves': Citerne,
  '/stations/clients': Handshake,
  '/stations/prix': Tags,
  '/stations/rapports': ClipboardList,
  '/stations/annexes': ShoppingCart,
  '/stations/reseau': MapPinned,
  '/stations/conformite': ShieldCheck,
  '/stations/reglages': Settings,
});

/** Icône de chaque entrée du menu Lots attribués (lot 057). */
const iconesLotsAttribues = () => ({
  '/lots': LayoutDashboard,
  '/lots/liste': Layers,
  '/lots/suivi': ListChecks,
  '/lots/retours': Undo2,
  '/lots/paiements': HandCoins,
});

/** Le menu du pack Stations sans son accueil (« / » mène déjà à la journée de la station). */
const menuStations = () => {
  const icones = iconesStations();
  return STATION_NAV_ITEMS.slice(1).map((item) => ({ ...item, icon: icones[item.to] || Activity }));
};

// Role-based navigation configuration
export const getNavItemsByRole = (user, isSuperAdmin, isAdminSociete, canAccessStations, canAccessFuelControl, canAccessTransit, canAccessLogistics, canAccessMaintenance, activePack, showOnboarding, managesDepot = true) => {
  // SUPER_ADMIN has a completely dedicated sidebar focused on SaaS platform management
  if (isSuperAdmin) {
    return [
      { to: '/', icon: Home, label: 'Accueil' },
      { to: '/organizations', icon: Building2, label: 'Organisations' },
      { to: '/societes-activite', icon: BarChart3, label: 'Activité des sociétés' },
      { to: '/users', icon: Users, label: 'Utilisateurs' },
      { to: '/licenses', icon: Key, label: 'Licences' },
      { to: '/audit', icon: Shield, label: 'Audit sécurité' },
      { to: '/journal', icon: ScrollText, label: 'Journal des actions' },
      { to: '/sante', icon: Activity, label: 'Santé de la plateforme' },
      { to: '/backend-ops', icon: Server, label: 'Opérations techniques' },
      { to: '/demonstration', icon: FlaskConical, label: 'Démonstration' },
      { to: '/import-intelligent', icon: Sparkles, label: 'Import intelligent' },
      { to: '/gps-providers', icon: Settings, label: 'Fournisseurs GPS', module: 'gps_tracking' },
      // WhatsApp delivery is its own integration, not a GPS setting.
      { to: '/openwa', icon: MessageCircle, label: 'Alertes WhatsApp' },
      { to: '/profile', icon: User, label: 'Profil' },
    ];
  }

  if (user?.role === 'CLIENT_PORTAL') {
    return [
      { to: '/client-portal', icon: PackageCheck, label: 'Portail client' },
      { to: '/profile', icon: User, label: 'Profil' },
    ];
  }

  const items = [];

  // Common items for all roles
  items.push({ to: '/', icon: LayoutDashboard, label: 'Accueil' });

  // Juste sous l'accueil, et seulement tant qu'il reste à faire: une entrée
  // permanente deviendrait du bruit dès la deuxième semaine, alors que sans
  // elle un nouvel administrateur n'a aucun point d'entrée dans un menu de
  // vingt lignes.
  if (isAdminSociete && showOnboarding) {
    items.push({ to: ONBOARDING_ROUTE, icon: Rocket, label: 'Démarrer' });
  }

  if (isControlFuelPrimaryPack(activePack)) {
    CONTROL_FUEL_NAV_ITEMS.slice(1).forEach((item) => {
      items.push({ ...item, icon: item.to.includes('sites') ? Building2 : item.to.includes('assets') ? Truck : Fuel });
    });
    items.push({ to: '/stock', icon: Warehouse, label: 'Stock interne', module: 'stock_movements' });
    items.push({ to: '/alerts', icon: Bell, label: 'Alertes', module: 'alerts' });
    items.push({ to: '/documents', icon: FileText, label: 'Documents', module: 'documents' });
    if (isAdminSociete) {
      items.push({ to: '/users', icon: Users, label: 'Utilisateurs' });
    }
    items.push({ to: '/profile', icon: User, label: 'Profil' });
    return items;
  }

  if (isStationsPrimaryPack(activePack)) {
    // L'accueil du pack (« / ») est la journée de la station : la première
    // entrée de la liste (« /stations ») ne se répète pas.
    menuStations().forEach((item) => items.push(item));
    items.push({ to: '/finance', icon: Wallet, label: LEXIQUE.menuArgent, module: 'finance_cockpit' });
    items.push({ to: '/alerts', icon: Bell, label: 'Alertes', module: 'alerts' });
    items.push({ to: '/documents', icon: FileText, label: 'Documents', module: 'documents' });
    if (isAdminSociete) {
      items.push({ to: '/users', icon: Users, label: 'Utilisateurs' });
    }
    items.push({ to: '/profile', icon: User, label: 'Profil' });
    return items;
  }

  // Transport: cinq groupes selon la vie d'un camion, puis « Plus » replié
  // (mémorisé dans le navigateur) pour ce qui se règle rarement.
  const menu = construireMenuTransport({
    isAdminSociete,
    canAccessTransit,
    canAccessLogistics,
    canAccessMaintenance,
    canAccessFuelControl,
    canAccessStations,
    managesDepot,
    ownStations: user?.entitlements?.own_stations === true,
    activePack,
  });
  items.push(...menu.groupes);
  const secondaires = [...menu.plus, ...menu.sectionsPacks];
  if (secondaires.length) {
    items.push({ type: 'more', label: LEXIQUE.menuPlus, icon: MoreHorizontal, items: secondaires });
  }

  // Profile (all roles)
  items.push({ to: '/profile', icon: User, label: 'Profil' });

  return items;
};

// Role-based mobile more items
export const getMobileMoreItemsByRole = (isSuperAdmin, isAdminSociete, canAccessStations, canAccessFuelControl, canAccessTransit, canAccessLogistics, canAccessMaintenance, activePack, showOnboarding, managesDepot = true, ownStations = false) => {
  const items = [];

  // SUPER_ADMIN items
  if (isSuperAdmin) {
    items.push({ to: '/societes-activite', icon: BarChart3, label: 'Activité des sociétés' });
    items.push({ to: '/audit', icon: Shield, label: 'Audit sécurité' });
    items.push({ to: '/journal', icon: ScrollText, label: 'Journal des actions' });
    items.push({ to: '/sante', icon: Activity, label: 'Santé de la plateforme' });
    items.push({ to: '/backend-ops', icon: Server, label: 'Opérations techniques' });
    items.push({ to: '/demonstration', icon: FlaskConical, label: 'Démonstration' });
    items.push({ to: '/import-intelligent', icon: Sparkles, label: 'Import intelligent' });
    items.push({ to: '/gps-providers', icon: Settings, label: 'Fournisseurs GPS', module: 'gps_tracking' });
    items.push({ to: '/openwa', icon: MessageCircle, label: 'Alertes WhatsApp' });
    return items;
  }

  // Le parcours doit rester joignable depuis un téléphone: c'est l'appareil
  // sur lequel une bonne partie des exploitants ouvrent le produit.
  const onboarding =
    isAdminSociete && showOnboarding
      ? [{ to: ONBOARDING_ROUTE, icon: Rocket, label: 'Démarrer' }]
      : [];

  if (isControlFuelPrimaryPack(activePack)) {
    return [
      ...onboarding,
      { to: '/control-fuel/sites', icon: Factory, label: 'Sites', module: 'sites' },
      { to: '/control-fuel/assets', icon: Truck, label: 'Engins', module: 'assets' },
      { to: '/control-fuel/requests', icon: FileText, label: 'Demandes', module: 'fuel_requests' },
      { to: '/control-fuel/issues', icon: Fuel, label: 'Sorties', module: 'fuel_issues' },
      { to: '/control-fuel/quotas', icon: Scale, label: 'Quotas', module: 'fuel_quotas' },
      { to: '/control-fuel/history', icon: FileText, label: 'Rapports', module: 'fuel_consumption' },
      { to: '/stock', icon: Warehouse, label: 'Stock interne', module: 'stock_movements' },
    ];
  }

  if (isStationsPrimaryPack(activePack)) {
    return [
      ...onboarding,
      ...menuStations(),
      { to: '/finance', icon: Wallet, label: LEXIQUE.menuArgent, module: 'finance_cockpit' },
    ];
  }

  items.push(...onboarding);

  // Même constructeur que la colonne de gauche, donc les mêmes écrans dans le
  // même ordre: un écran joignable sur un appareil l'est sur l'autre. Chaque
  // entrée porte le titre de son groupe, que la feuille « Plus » affiche en
  // intertitre.
  const menu = construireMenuTransport({
    isAdminSociete,
    canAccessTransit,
    canAccessLogistics,
    canAccessMaintenance,
    canAccessFuelControl,
    canAccessStations,
    managesDepot,
    ownStations,
    activePack,
  });
  menu.groupes.forEach((groupe) => {
    groupe.items.forEach((item) => items.push({ ...item, groupe: groupe.label }));
  });
  menu.plus.forEach((item) => items.push({ ...item, groupe: LEXIQUE.menuPlus }));
  // La section Contrôle carburant devient une entrée sur téléphone: l'écran
  // d'accueil du pack mène au reste. Les stations sont dans « Mes stations ».
  if (canAccessFuelControl) {
    items.push({ to: '/control-fuel', icon: Fuel, label: 'Contrôle carburant', module: 'fuel_consumption', groupe: LEXIQUE.menuPlus });
  }

  return items;
};

// Role-based mobile nav items
export const getMobileNavItemsByRole = (isSuperAdmin, isAdminSociete, canAccessTransit, activePack, userRole) => {
  // SUPER_ADMIN has dedicated items
  if (isSuperAdmin) {
    return [
      { to: '/', icon: LayoutDashboard, label: 'Accueil' },
      { to: '/organizations', icon: Building2, label: 'Organisations' },
      { to: '/users', icon: Users, label: 'Utilisateurs' },
      { to: '/licenses', icon: Key, label: 'Licences' },
    ];
  }

  if (userRole === 'CLIENT_PORTAL') {
    return [
      { to: '/client-portal', icon: PackageCheck, label: 'Portail' },
    ];
  }

  // Common items for all roles
  const items = [
    { to: '/', icon: LayoutDashboard, label: 'Accueil' },
  ];

  if (isControlFuelPrimaryPack(activePack)) {
    items.push({ to: '/control-fuel/issues', icon: Fuel, label: 'Sorties', module: 'fuel_issues' });
    items.push({ to: '/control-fuel/requests', icon: FileText, label: 'Demandes', module: 'fuel_requests' });
    items.push({ to: '/stock', icon: Warehouse, label: 'Stock', module: 'stock_movements' });
    return items;
  }

  if (isStationsPrimaryPack(activePack)) {
    // Barre du bas du gérant : le poste, les cuves, le rapport.
    const icones = iconesStations();
    items.push({ to: '/stations/poste', icon: icones['/stations/poste'], label: 'Poste', module: 'shifts' });
    items.push({ to: '/stations/cuves', icon: icones['/stations/cuves'], label: 'Cuves', module: 'tanks' });
    items.push({ to: '/stations/rapports', icon: icones['/stations/rapports'], label: 'Rapports', module: 'station_daily_report' });
    return items;
  }

  // Barre du bas: les trois écrans du quotidien après l'accueil — les camions,
  // les trajets, les commandes. Tout le reste est dans la feuille « Plus ».
  if (isAdminSociete || canAccessTransit) {
    items.push({ to: '/trucks', icon: Truck, label: LEXIQUE.menuCamions, module: 'trucks' });
    items.push({ to: '/trips', icon: Map, label: LEXIQUE.menuTrajets, module: 'trips' });
    items.push({ to: '/orders', icon: ShoppingCart, label: LEXIQUE.menuCommandes, module: 'orders' });
  }

  return items;
};

// Modules the organisation has not subscribed to are dropped, not shown behind
// a padlock. A greyed entry that refuses every click is still an entry: it has
// to be read and dismissed on every scan of the menu, and on a licence like
// Contrôle — which leaves most of the transport and station modules off — it
// buried the handful of usable screens in a list of doors that do not open.
// The sidebar now lists what this organisation can actually do.
export const filterNavItemsByModule = (items, hasModule, canSeeMoney = false) =>
  items.reduce((kept, item) => {
    // Une entrée marquée `argent` (Paiements des lots) n'est proposée qu'à qui
    // voit les montants: le serveur refuse les autres de toute façon.
    if (item.argent && canSeeMoney !== true) return kept;
    // Le bloc « Plus » contient lui-même des sections: il se filtre donc par
    // récursion, sans quoi une licence sans stations garderait l'en-tête
    // « Stations » à l'intérieur du repli — invisible au premier coup d'œil,
    // et donc jamais corrigé.
    if (item.type === 'more' && item.items) {
      const children = filterNavItemsByModule(item.items, hasModule, canSeeMoney);
      if (children.length) kept.push({ ...item, items: children });
      return kept;
    }
    if (item.type === 'section' && item.items) {
      const children = item.items.filter(
        (child) => (!child.module || hasModule(child.module)) && (!child.argent || canSeeMoney === true),
      );
      // An empty section header would announce a category with nothing in it.
      if (children.length) kept.push({ ...item, items: children });
      return kept;
    }
    if (!item.module || hasModule(item.module)) kept.push(item);
    return kept;
  }, []);

/**
 * Le repli du bloc « Plus » survit au rafraîchissement.
 *
 * Même motif que le parcours de démarrage (`components/onboarding.js`): une
 * clé versionnée, du JSON, et un `try` autour du stockage — refusé en
 * navigation privée, il ne doit pas emporter la barre de navigation avec lui.
 * Sans persistance, quelqu'un qui travaille dans les écrans de réglage
 * rouvrirait le bloc à chaque page: le repli redeviendrait un obstacle au lieu
 * d'être un rangement.
 */
const CLE_NAV_PLUS = 'ecarbu.nav.plus.v1';

const lireNavPlusOuvert = () => {
  try {
    const brut = window.localStorage.getItem(CLE_NAV_PLUS);
    const lu = brut ? JSON.parse(brut) : null;
    return lu?.ouvert === true;
  } catch (_) {
    // Fermé par défaut: c'est l'état qui rend la colonne courte, et c'est tout
    // l'objet du bloc.
    return false;
  }
};

const ecrireNavPlusOuvert = (ouvert) => {
  try {
    window.localStorage.setItem(CLE_NAV_PLUS, JSON.stringify({ ouvert: ouvert === true }));
  } catch (_) {
    /* Stockage plein ou refusé : seul le souvenir du repli se perd. */
  }
};

const moreGridVariants = {
  hidden: { opacity: 0, y: 12 },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      staggerChildren: 0.08,
      delayChildren: 0.05,
    },
  },
};

const moreItemVariants = {
  hidden: { opacity: 0, y: 10, scale: 0.98 },
  show: { opacity: 1, y: 0, scale: 1 },
};



/**
 * Le bloc « Plus » : tout ce qui ne sert pas tous les jours, replié.
 *
 * Il ne se déplie pas tout seul quand on est sur l'un de ses écrans, à la
 * différence d'une section: on y arrive par un lien ou par l'URL, et voir la
 * colonne s'ouvrir de vingt lignes sous les pieds à chaque navigation est
 * exactement ce que ce bloc doit éviter. Le repli reste une décision de
 * l'utilisateur, et elle est retenue.
 */
/**
 * La colonne repliée: les icônes seules, les libellés en infobulle.
 *
 * Sur un portable de 1280 px, la barre latérale prend un cinquième de la
 * largeur en permanence — or on n'y navigue que quelques secondes par heure.
 * Le repli rend cette place à l'écran qu'on regarde vraiment, sans rien
 * cacher: chaque destination garde son icône et son titre au survol.
 *
 * Un contexte plutôt que des accessoires: `NavItem` est rendu depuis trois
 * endroits différents, dont l'intérieur de `NavPlus`, et faire descendre un
 * booléen à travers les trois ferait passer par des composants qui n'ont rien
 * à en faire.
 */
const ReplieContext = React.createContext(false);

const CLE_NAV_REPLIE = 'ecarbu.nav.replie.v1';

const lireNavReplie = () => {
  try {
    const brut = window.localStorage.getItem(CLE_NAV_REPLIE);
    return brut ? JSON.parse(brut)?.replie === true : false;
  } catch (_) {
    return false;
  }
};

const ecrireNavReplie = (replie) => {
  try {
    window.localStorage.setItem(
      CLE_NAV_REPLIE,
      JSON.stringify({ replie: replie === true }),
    );
  } catch (_) {
    /* Stockage plein ou refusé: le repli marche, il ne survit pas au rechargement. */
  }
};

const NavPlus = ({ label, icon: Icon, items }) => {
  const [ouvert, setOuvert] = useState(lireNavPlusOuvert);
  const replie = React.useContext(ReplieContext);

  const basculer = () => {
    setOuvert((precedent) => {
      const suivant = !precedent;
      ecrireNavPlusOuvert(suivant);
      return suivant;
    });
  };

  return (
    <div className="space-y-0.5 pt-1">
      <button
        type="button"
        onClick={basculer}
        className={cn(
          'flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors',
          'text-muted-foreground hover:bg-accent hover:text-foreground',
        )}
        aria-expanded={ouvert}
        title={replie ? label : undefined}
      >
        <span aria-hidden="true" className="-ml-3 h-5 w-0.5 rounded-full bg-transparent" />
        <Icon className="h-4 w-4 shrink-0" />
        {!replie && (
          <>
            <span className="line-clamp-2 min-w-0 text-left leading-snug">{label}</span>
            <ChevronRight
              className={cn(
                'ml-auto h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform duration-200',
                ouvert ? 'rotate-90' : '',
              )}
              aria-hidden="true"
            />
          </>
        )}
      </button>
      {(ouvert || replie) && (
        <div className="space-y-0.5">
          {items.map((item, index) => (
            <NavItem key={item.to || `section-${index}`} {...item} />
          ))}
        </div>
      )}
    </div>
  );
};

const NavItem = ({ to, icon: Icon, label, items, type, onClick, defaultOpen, badgeCount }) => {
  const location = useLocation();
  const replie = React.useContext(ReplieContext);
  const isSection = type === 'section';
  const sectionActive = isSection && items
    ? items.some((si) => location.pathname === si.to || location.pathname.startsWith(si.to + '/'))
    : false;
  // Les groupes du menu de processus sont dépliés d'emblée: l'exploitant voit
  // tout le parcours d'un coup d'œil, comme les onglets d'un classeur.
  const [isOpen, setIsOpen] = useState(sectionActive || defaultOpen === true);

  useEffect(() => {
    if (sectionActive) setIsOpen(true);
  }, [sectionActive]);

  // Colonne repliée: les en-têtes de groupe n'ont plus de place, chaque écran
  // reste joignable par son icône, libellé en infobulle.
  if (isSection && items && replie) {
    return (
      <div className="space-y-0.5">
        {items.map((subItem) => (
          <NavItem
            key={subItem.to}
            to={subItem.to}
            icon={subItem.icon || Icon}
            label={subItem.label}
          />
        ))}
      </div>
    );
  }

  if (isSection && items) {
    return (
      <div className="space-y-1">
        <button
          onClick={() => setIsOpen((v) => !v)}
          className={cn(
            'flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors',
            sectionActive
              ? 'font-semibold text-foreground'
              : 'text-muted-foreground hover:bg-accent hover:text-foreground',
          )}
          aria-expanded={isOpen}
        >
          <span
            aria-hidden="true"
            className={cn(
              '-ml-3 h-5 w-0.5 rounded-full transition-colors',
              sectionActive ? 'bg-flame' : 'bg-transparent',
            )}
          />
          <Icon className="h-4 w-4 shrink-0" />
          <span className="line-clamp-2 min-w-0 text-left leading-snug">{label}</span>
          <ChevronRight
            className={cn(
              'ml-auto h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform duration-200',
              isOpen ? 'rotate-90' : '',
            )}
            aria-hidden="true"
          />
        </button>
        {isOpen && (
          <div className="ml-4 space-y-0.5 border-l border-border pl-2">
            {items.map((subItem) => {
              return (
                <NavLink
                  key={subItem.to}
                  to={subItem.to}
                  end={items.some((autre) => autre !== subItem && autre.to.startsWith(`${subItem.to}/`))}
                  className={({ isActive }) =>
                    cn(
                      'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                      'hover:bg-accent hover:text-foreground',
                      isActive
                        ? 'bg-flame/12 font-semibold text-flame-ink'
                        : 'text-muted-foreground',
                    )
                  }
                >
                  {subItem.icon && <subItem.icon className="h-4 w-4 shrink-0" aria-hidden="true" />}
                  <span className="line-clamp-2 min-w-0 leading-snug">{subItem.label}</span>
                </NavLink>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  return (
    <NavLink
      to={to}
      end={to === '/'}
      onClick={onClick}
      title={replie ? label : undefined}
      className={({ isActive }) =>
        cn(
          'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors',
          'hover:bg-accent hover:text-foreground',
          isActive
            ? 'bg-flame/12 font-semibold text-flame-ink'
            : 'text-muted-foreground',
        )
      }
    >
      {/* The active rail, not a filled block: the sidebar is scanned, and a
          solid orange row shouts louder than the page content it points to. */}
      {({ isActive }) => (
        <>
          <span
            aria-hidden="true"
            className={cn(
              '-ml-3 h-5 w-0.5 rounded-full transition-colors',
              isActive ? 'bg-flame' : 'bg-transparent',
            )}
          />
          {/* Une seule famille d'icônes dans cette colonne. Le kit de marque
              ne couvre que dix-huit destinations sur la trentaine du menu:
              Licences, Audit sécurité et Opérations techniques n'en ont aucune.
              Mélangées, les deux familles se voyaient sans que rien ne
              l'explique — deux épaisseurs de trait, deux façons de dessiner un
              document. Mieux vaut une seule main. */}
          <Icon className="h-4 w-4 shrink-0" />
          {!replie && <span className="line-clamp-2 min-w-0 leading-snug">{label}</span>}
          {Number.isFinite(badgeCount) && badgeCount > 0 ? (
            replie ? (
              <span aria-hidden="true" className="-ml-2 h-2 w-2 shrink-0 rounded-full bg-flame" />
            ) : (
              <span className="ml-auto shrink-0 rounded-full bg-flame/15 px-1.5 py-0.5 font-mono text-[10px] font-bold text-flame-ink">
                {badgeCount}
              </span>
            )
          ) : null}
        </>
      )}
    </NavLink>
  );
};

const Layout = ({ children }) => {
  const { user, logout, isSuperAdmin, isAdminSociete, canAccessStations, canAccessFuelControl, canAccessTransit, canAccessLogistics, canAccessMaintenance, hasModule, activePack, managesDepot, canSeeMoney } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [theme, setTheme] = useState('light');
  const [navReplie, setNavReplie] = useState(lireNavReplie);
  const [badgeCounts, setBadgeCounts] = useState({
    invoices: 0,
    stock: 0,
    operations: 0,
  });

  // Relu à chaque changement de route: le parcours se termine sur son propre
  // écran, et c'est en le quittant que la barre doit cesser de le proposer.
  const [showOnboarding, setShowOnboarding] = useState(() => !isOnboardingDone());
  useEffect(() => {
    setShowOnboarding(!isOnboardingDone());
  }, [location.pathname]);

  // Get role-based navigation items
  const roleBasedNavItems = useMemo(() => {
    return getNavItemsByRole(user, isSuperAdmin, isAdminSociete, canAccessStations, canAccessFuelControl, canAccessTransit, canAccessLogistics, canAccessMaintenance, activePack, showOnboarding, managesDepot);
  }, [user, isSuperAdmin, isAdminSociete, canAccessStations, canAccessFuelControl, canAccessTransit, canAccessLogistics, canAccessMaintenance, activePack, showOnboarding, managesDepot]);

  // Pastilles du propriétaire de la plateforme : licences à renouveler et
  // alertes de sécurité des dernières 24 h. Relues toutes les cinq minutes.
  const [compteursPlateforme, setCompteursPlateforme] = useState({ licences: 0, securite: 0 });
  useEffect(() => {
    if (!isSuperAdmin) return undefined;
    let actif = true;
    const charger = async () => {
      const [licences, anomalies] = await Promise.allSettled([
        licensesAPI.getExpiringSoon(30),
        adminAPI.getAnomalies(24),
      ]);
      if (!actif) return;
      setCompteursPlateforme({
        licences: licences.status === 'fulfilled' && Array.isArray(licences.value) ? licences.value.length : 0,
        securite: anomalies.status === 'fulfilled' ? Number(anomalies.value?.total_alerts) || 0 : 0,
      });
    };
    charger();
    const minuteur = setInterval(charger, 5 * 60 * 1000);
    return () => {
      actif = false;
      clearInterval(minuteur);
    };
  }, [isSuperAdmin]);
  const avecPastillesPlateforme = useCallback(
    (liste) =>
      isSuperAdmin
        ? liste.map((item) => {
            if (item.to === '/licenses') return { ...item, badgeCount: compteursPlateforme.licences };
            if (item.to === '/audit') return { ...item, badgeCount: compteursPlateforme.securite };
            return item;
          })
        : liste,
    [isSuperAdmin, compteursPlateforme],
  );

  const displayNavItems = avecPastillesPlateforme(filterNavItemsByModule(roleBasedNavItems, hasModule, canSeeMoney));

  useEffect(() => {
    const storedTheme = localStorage.getItem('theme') || 'light';
    setTheme(storedTheme);
    document.documentElement.classList.remove('light', 'dark');
    document.documentElement.classList.add(storedTheme);
  }, []);

  const toggleTheme = () => {
    const nextTheme = theme === 'dark' ? 'light' : 'dark';
    setTheme(nextTheme);
    localStorage.setItem('theme', nextTheme);
    document.documentElement.classList.remove('light', 'dark');
    document.documentElement.classList.add(nextTheme);
  };

  // Les pastilles du menu suivent les écritures (facture payée, mouvement de
  // stock, convoi clôturé) au lieu d'attendre le prochain tour du minuteur.
  const [versionCompteurs, setVersionCompteurs] = useState(0);
  useRafraichirSur(['factures', 'stock', 'convois'], () => setVersionCompteurs((v) => v + 1));

  useEffect(() => {
    let mounted = true;

    const loadCounts = async () => {
      try {
        const weekStart = new Date();
        weekStart.setHours(0, 0, 0, 0);
        weekStart.setDate(weekStart.getDate() - 6);
        const startOfWeek = weekStart.toISOString();

        const [invoicesCount, stockCount, operationsCount] = await Promise.all([
          db.invoices.where('status').anyOf(['PENDING', 'OVERDUE']).count(),
          db.stockMovements
            .where('movement_date')
            .aboveOrEqual(startOfWeek)
            .count(),
          // IndexedDB does not allow boolean keys → filter in JS.
          db.operations.filter((o) => o.active === true).count(),
        ]);
        
        if (mounted) {
          setBadgeCounts({
            invoices: invoicesCount,
            stock: stockCount,
            operations: operationsCount,
          });
        }
      } catch (err) {
        // Silent fail for badge counts
      }
    };

    loadCounts();
    const interval = setInterval(loadCounts, 15000);
    return () => {
      mounted = false;
      clearInterval(interval);
    };
  }, [versionCompteurs]);

  const mobileMoreItems = useMemo(
    () => {
      const filteredItems = filterNavItemsByModule(
        getMobileMoreItemsByRole(isSuperAdmin, isAdminSociete, canAccessStations, canAccessFuelControl, canAccessTransit, canAccessLogistics, canAccessMaintenance, activePack, showOnboarding, managesDepot, user?.entitlements?.own_stations === true),
        hasModule,
        canSeeMoney,
      );
      return avecPastillesPlateforme(filteredItems).map((item) => {
        if (item.to === '/invoices')
          return { ...item, badgeCount: badgeCounts.invoices };
        if (item.to === '/stock')
          return { ...item, badgeCount: badgeCounts.stock };
        if (item.to === '/operations')
          return { ...item, badgeCount: badgeCounts.operations };
        return item;
      });
    },
    [badgeCounts, avecPastillesPlateforme, isSuperAdmin, isAdminSociete, canAccessStations, canAccessFuelControl, canAccessTransit, canAccessLogistics, canAccessMaintenance, hasModule, canSeeMoney, activePack, showOnboarding, managesDepot, user?.entitlements?.own_stations],
  );

  const mobileNavItems = useMemo(
    () => filterNavItemsByModule(
      getMobileNavItemsByRole(isSuperAdmin, isAdminSociete, canAccessTransit, activePack, user?.role),
      hasModule,
      canSeeMoney,
    ),
    [isSuperAdmin, isAdminSociete, canAccessTransit, hasModule, canSeeMoney, activePack, user?.role],
  );

  // The bottom bar holds four destinations; anything the role-based list
  // returns beyond that moves into the "Plus" sheet rather than wrapping the
  // bar onto a second row.
  const primaryMobileItems = useMemo(
    () => mobileNavItems.slice(0, 4),
    [mobileNavItems],
  );

  // La feuille « Plus » liste tout ce que la barre du bas ne porte pas, dans
  // l'ordre des groupes du menu de bureau.
  const overflowMobileItems = useMemo(() => {
    const shown = new Set(primaryMobileItems.map((i) => i.to));
    const extra = mobileNavItems.filter((i) => !shown.has(i.to));
    return [...extra, ...mobileMoreItems]
      .filter((item) => !shown.has(item.to))
      .filter((item, idx, arr) => arr.findIndex((x) => x.to === item.to) === idx);
  }, [mobileNavItems, primaryMobileItems, mobileMoreItems]);

  const initials = useMemo(() => {
    const source = user?.full_name || user?.email || '';
    const parts = source.trim().split(/[\s@._-]+/).filter(Boolean);
    if (!parts.length) return '—';
    return parts.slice(0, 2).map((p) => p[0]).join('').toUpperCase();
  }, [user?.full_name, user?.email]);

  const roleLabel = ROLE_LABELS[user?.role] || 'Utilisateur';
  const packLabel = PACK_LABELS[activePack] || 'Logistique';

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Keyboard users land here first and can jump past 30+ nav links. */}
      <a
        href="#contenu"
        className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[60] focus:rounded-lg focus:bg-primary focus:px-4 focus:py-2 focus:text-sm focus:font-semibold focus:text-primary-foreground"
      >
        Aller au contenu
      </a>

      {/* Noise texture overlay */}
      <div className="noise-overlay fixed inset-0 pointer-events-none z-0" />

      {/* Desktop Sidebar */}
      <ReplieContext.Provider value={navReplie}>
      <aside
        className={cn(
          'fixed bottom-0 left-0 top-0 z-40 hidden flex-col border-r border-border bg-card transition-[width] duration-200 lg:flex',
          navReplie ? 'w-[4.5rem]' : 'w-64',
        )}
      >
        <div
          className={cn(
            'flex items-center gap-3 border-b border-border py-4',
            navReplie ? 'justify-center px-3' : 'px-5',
          )}
        >
          <BrandLogo variant="plain" size={38} />
          {!navReplie && (
            <div className="min-w-0">
              <p className="font-display text-base font-bold leading-none tracking-tight text-foreground">
                E-CARBU
              </p>
              <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.14em] text-muted-foreground">
                {packLabel}
              </p>
            </div>
          )}
        </div>

        {/* Le bouton vit au bord de la colonne, là où l'on va chercher sa
            limite. Posé dans l'en-tête, il se serait disputé la place avec le
            logo une fois la colonne réduite à soixante-douze pixels. */}
        <button
          type="button"
          onClick={() => {
            setNavReplie((precedent) => {
              const suivant = !precedent;
              ecrireNavReplie(suivant);
              return suivant;
            });
          }}
          className="absolute -right-3 top-16 z-10 hidden h-6 w-6 items-center justify-center rounded-full border border-border bg-card text-muted-foreground shadow-sm transition-colors hover:text-foreground lg:flex"
          aria-label={navReplie ? 'Déplier le menu' : 'Replier le menu'}
          title={navReplie ? 'Déplier le menu' : 'Replier le menu'}
          data-testid="toggle-sidebar-btn"
        >
          <ChevronRight
            className={cn(
              'h-3.5 w-3.5 transition-transform duration-200',
              navReplie ? '' : 'rotate-180',
            )}
            aria-hidden="true"
          />
        </button>

        <nav
          className={cn(
            'flex-1 space-y-0.5 overflow-y-auto py-4',
            navReplie ? 'px-2' : 'px-4',
          )}
          aria-label="Navigation principale"
        >
          {displayNavItems.map((item, index) =>
            item.type === 'more' ? (
              <NavPlus key="nav-plus" {...item} />
            ) : (
              <NavItem key={item.to || `section-${index}`} {...item} />
            ),
          )}
        </nav>

        <div className={cn('space-y-3 border-t border-border', navReplie ? 'p-2' : 'p-4')}>
          <div className={cn('flex items-center gap-3', navReplie && 'flex-col gap-2')}>
            <div
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 font-display text-sm font-bold text-primary"
              title={navReplie ? user?.full_name || user?.email || 'Compte' : undefined}
            >
              {initials}
            </div>
            {!navReplie && (
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold text-foreground">
                  {user?.full_name || user?.email || 'Compte'}
                </p>
                <p className="truncate text-xs text-muted-foreground">{roleLabel}</p>
              </div>
            )}
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              aria-label={theme === 'dark' ? 'Passer en thème clair' : 'Passer en thème sombre'}
              title={theme === 'dark' ? 'Thème clair' : 'Thème sombre'}
            >
              {theme === 'dark' ? (
                <Sun className="h-4 w-4" />
              ) : (
                <Moon className="h-4 w-4" />
              )}
            </Button>
          </div>
          <Button
            variant="outline"
            size="sm"
            className={cn('w-full gap-2', navReplie ? 'justify-center px-0' : 'justify-start')}
            onClick={handleLogout}
            title={navReplie ? 'Déconnexion' : undefined}
            data-testid="logout-btn"
          >
            <LogOut className="h-4 w-4" />
            {!navReplie && 'Déconnexion'}
          </Button>
        </div>
      </aside>
      </ReplieContext.Provider>

      {/* Mobile Header */}
      <header className="fixed inset-x-0 top-0 z-40 flex h-14 items-center justify-between border-b border-border bg-card px-4 lg:hidden">
        <div className="flex shrink-0 items-center gap-2.5">
          <BrandLogo variant="plain" size={30} />
          <span className="hidden whitespace-nowrap font-display text-sm font-bold tracking-tight text-foreground min-[440px]:inline">
            E-CARBU
          </span>
        </div>
        <div className="flex min-w-0 items-center gap-1">
          <SyncIndicator inline className="min-w-0 lg:hidden max-[359px]:[&_[data-testid=last-sync-time]]:hidden" />
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleTheme}
            aria-label={theme === 'dark' ? 'Passer en thème clair' : 'Passer en thème sombre'}
          >
            {theme === 'dark' ? (
              <Sun className="h-5 w-5" />
            ) : (
              <Moon className="h-5 w-5" />
            )}
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/profile')}
            aria-label="Mon profil"
          >
            <User className="h-5 w-5" />
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <main
        id="contenu"
        className={cn(
          'relative z-10 flex min-h-screen flex-col transition-[margin] duration-200',
          navReplie ? 'lg:ml-[4.5rem]' : 'lg:ml-64',
        )}
      >
        {/* La barre supérieure est dans le flux et non fixée: `sticky` suffit,
            et une barre fixée aurait demandé de compenser sa hauteur sur les
            quarante-six écrans — une marge à ne jamais oublier, donc une marge
            qu'on oublie. */}
        <TopBar />
        {/* Changement d'écran: le menu et la barre restent en place, seul le
            contenu apparaît en fondu court. L'ancienne transition faisait
            disparaître toute l'application, d'où un écran noir à chaque clic. */}
        <div
          key={location.pathname}
          className="entree-page flex-1 pt-14 lg:pt-0 pb-[calc(4.5rem+env(safe-area-inset-bottom))] lg:pb-0"
        >
          {children}
        </div>
        {/* Le pied ferme la page plutôt que de flotter: il ne porte rien
            d'urgent, seulement de quoi se repérer et demander de l'aide. */}
        <footer className="hidden items-center justify-between gap-4 border-t border-border px-4 py-4 text-xs text-muted-foreground md:px-6 lg:flex lg:pl-8 lg:pr-24">
          <p>
            <span className="font-semibold text-foreground">E-CARBU Transport</span>
            {' · '}
            <span className="italic">Une logistique plus simple, plus performante.</span>
          </p>
          <div className="flex items-center gap-4">
            <span className="tabular-nums">Version {APP_VERSION}</span>
            <button
              type="button"
              onClick={() => navigate(ONBOARDING_ROUTE)}
              className="inline-flex items-center gap-1.5 rounded-lg px-2 py-1 font-medium transition-colors hover:bg-muted hover:text-foreground"
            >
              <BookOpen className="h-3.5 w-3.5" aria-hidden="true" />
              Besoin d’aide ?
            </button>
          </div>
        </footer>
      </main>

      {/* Mobile Bottom Nav */}
      <nav
        className="fixed inset-x-0 bottom-0 z-40 border-t border-border bg-card pb-[env(safe-area-inset-bottom)] lg:hidden"
        aria-label="Navigation rapide"
      >
        {/* Four destinations plus "Plus": the role-based list can return five
            items, which used to overflow the five-column grid onto a second
            row. The overflow belongs in the sheet, not in a taller bar. */}
        <div className="grid grid-cols-5 gap-1 px-1.5 py-1.5">
          {primaryMobileItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/'}
              className={({ isActive }) =>
                cn(
                  'flex min-h-[52px] flex-col items-center justify-center gap-1 rounded-lg px-1 py-1.5 text-[10px] font-medium transition-colors',
                  isActive
                    ? 'bg-flame/12 text-flame-ink'
                    : 'text-muted-foreground',
                )
              }
            >
              <item.icon className="h-5 w-5 shrink-0" aria-hidden="true" />
              <span className="w-full truncate text-center leading-none">
                {item.label}
              </span>
            </NavLink>
          ))}

          <Sheet>
            <SheetTrigger asChild>
              <button
                type="button"
                className={cn(
                  'flex min-h-[52px] flex-col items-center justify-center gap-1 rounded-lg px-1 py-1.5 text-[10px] font-medium text-muted-foreground',
                  'transition-colors hover:bg-accent hover:text-foreground',
                )}
              >
                <Menu className="h-5 w-5 shrink-0" aria-hidden="true" />
                <span className="leading-none">Plus</span>
              </button>
            </SheetTrigger>
            <SheetContent
              side="bottom"
              className="max-h-[80vh] overflow-y-auto border-border bg-card"
            >
              <SheetHeader>
                <SheetTitle className="text-left">{LEXIQUE.menuTousLesEcrans}</SheetTitle>
              </SheetHeader>
              <motion.div
                className="mt-4 grid grid-cols-2 gap-2 pb-[env(safe-area-inset-bottom)]"
                variants={moreGridVariants}
                initial="hidden"
                animate="show"
              >
                {overflowMobileItems.map((item, index) => (
                  <React.Fragment key={item.to}>
                  {item.groupe && item.groupe !== overflowMobileItems[index - 1]?.groupe && (
                    <p className="col-span-2 mt-2 font-mono text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground first:mt-0">
                      {item.groupe}
                    </p>
                  )}
                  <motion.div variants={moreItemVariants}>
                    <SheetClose asChild>
                      <NavLink
                        to={item.to}
                        className={cn(
                          'relative flex items-center gap-3 rounded-xl border border-border bg-card px-3 py-3',
                          'text-sm text-foreground transition-colors hover:border-primary/40 hover:bg-accent',
                        )}
                      >
                        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
                          <item.icon className="h-4 w-4" aria-hidden="true" />
                        </span>
                        <span className="line-clamp-2 min-w-0 flex-1 break-words font-medium leading-snug">
                          {item.label}
                        </span>
                        {Number.isFinite(item.badgeCount) && item.badgeCount > 0 ? (
                          <span className="shrink-0 rounded-full bg-flame/15 px-1.5 py-0.5 font-mono text-[10px] font-bold text-flame-ink">
                            {item.badgeCount}
                          </span>
                        ) : null}
                      </NavLink>
                    </SheetClose>
                  </motion.div>
                  </React.Fragment>
                ))}
              </motion.div>
            </SheetContent>
          </Sheet>
        </div>
      </nav>

      {/* L'indicateur flottant ne sert plus sur grand écran: la barre
          supérieure le porte désormais, à un endroit fixe et sans recouvrir la
          page. Flottant en bas à droite, il masquait le lien de sortie des
          cartes qui s'y terminent — on le voyait sur l'accueil, posé sur
          « Voir tous ». */}

      {/* Assistant (spec 043): le bot WhatsApp dans l'application, pour les
          rôles du bot. Le serveur dit s'il est ouvert à ce compte; sinon rien
          ne s'affiche. Le pied de page lui laisse sa place à droite. */}
      {!isSuperAdmin && user?.role !== 'CLIENT_PORTAL' ? <AssistantChat /> : null}
    </div>
  );
};

export default Layout;
